import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import UserService from './services/UserService';

const getLast30DaysRange = () => {
  const now = new Date();
  const prior = new Date();
  prior.setDate(prior.getDate() - 30);
  const formatDate = (d) => d.toISOString().slice(0, 10);
  return {
    startDate: formatDate(prior),
    endDate: formatDate(now),
  };
};

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [role, setRole] = useState('All');
  const [activePreset, setActivePreset] = useState('last30');
  const [startDate, setStartDate] = useState(() => getLast30DaysRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DaysRange().endDate);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const reduxUser = useSelector((state) => state?.auth?.user);

  // Check access authorization
  const getStoredUser = () => {
    try {
      const s = localStorage.getItem('user') || localStorage.getItem('profile');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  };

  const storedUser = reduxUser || getStoredUser();
  const viewAsRole = sessionStorage.getItem('mtc_view_as_role');

  const { hasAccess } = useMemo(() => {
    if (viewAsRole) {
      const v = viewAsRole.toLowerCase();
      const adminSim = v === 'admin';
      const presSim = v === 'president';
      return { isAdmin: adminSim, isPresident: presSim, hasAccess: adminSim || presSim };
    }

    if (!storedUser) return { isAdmin: false, isPresident: false, hasAccess: false };
    if (Boolean(storedUser.isAdmin)) return { isAdmin: true, isPresident: false, hasAccess: true };
    if (Boolean(storedUser.isPresident)) return { isAdmin: false, isPresident: true, hasAccess: true };

    const parts = [];
    if (Array.isArray(storedUser.roles)) parts.push(...storedUser.roles);
    else if (storedUser.roles) parts.push(storedUser.roles);

    if (Array.isArray(storedUser.authorities)) parts.push(...storedUser.authorities);
    else if (storedUser.authorities) parts.push(storedUser.authorities);

    if (Array.isArray(storedUser.role)) parts.push(...storedUser.role);
    else if (storedUser.role) parts.push(storedUser.role);

    if (storedUser.roleName) parts.push(storedUser.roleName);
    if (storedUser.name) parts.push(storedUser.name);

    const admin = parts.some((r) => {
      const str = (typeof r === 'object' && r !== null ? (r.name || r.authority || String(r)) : String(r)).toLowerCase();
      return str.includes('admin');
    }) || String(storedUser.username || '').toLowerCase() === 'admin' || String(storedUser.email || '').toLowerCase().startsWith('admin@');

    const pres = parts.some((r) => {
      const str = (typeof r === 'object' && r !== null ? (r.name || r.authority || String(r)) : String(r)).toLowerCase();
      return str.includes('president');
    });

    return {
      isAdmin: admin,
      isPresident: pres,
      hasAccess: admin || pres
    };
  }, [storedUser, viewAsRole]);

  const fetchLogs = useCallback(() => {
    if (!hasAccess) return;
    setLoading(true);
    setError('');

    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: search.trim() || undefined,
      category: category !== 'All' ? category : undefined,
      role: role !== 'All' ? role : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    };

    UserService.getActivityLogs(params)
      .then((res) => {
        setLogs(res.data?.logs || []);
        setTotal(res.data?.total || 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load activity logs:', err);
        setError(err?.response?.data?.message || err?.message || 'Access denied or failed to load activity logs.');
        setLoading(false);
      });
  }, [hasAccess, page, rowsPerPage, search, category, role, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearch('');
    setCategory('All');
    setRole('All');
    const { startDate: s, endDate: e } = getLast30DaysRange();
    setStartDate(s);
    setEndDate(e);
    setActivePreset('last30');
    setPage(0);
  };

  const setPresetRange = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'thisMonth') {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      setStartDate(`${year}-${month}-01`);
      setEndDate(`${year}-${month}-${String(lastDay).padStart(2, '0')}`);
    } else if (preset === 'last30') {
      const { startDate: s, endDate: e } = getLast30DaysRange();
      setStartDate(s);
      setEndDate(e);
    } else if (preset === 'thisYear') {
      const year = now.getFullYear();
      setStartDate(`${year}-01-01`);
      setEndDate(now.toISOString().slice(0, 10));
    }
    setPage(0);
  };

  const getRoleChipColor = (officerRole) => {
    const r = String(officerRole || '').toLowerCase();
    if (r.includes('admin')) return 'error';
    if (r.includes('president')) return 'secondary';
    if (r.includes('financial')) return 'primary';
    if (r.includes('registrar') || r.includes('secretary')) return 'warning';
    if (r.includes('business')) return 'info';
    if (r.includes('user')) return 'default';
    return 'default';
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Allocation':
        return '#0284c7';
      case 'Savings':
        return '#16a34a';
      case 'Shares':
        return '#9333ea';
      case 'Loan':
        return '#ea580c';
      case 'Business':
        return '#0d9488';
      case 'Member Profile':
        return '#4f46e5';
      case 'Security':
        return '#dc2626';
      case 'President':
        return '#059669';
      default:
        return '#64748b';
    }
  };

  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ fontSize: '1rem', py: 2 }}>
          <strong>Access Restricted:</strong> Activity and audit logs are strictly restricted to Administrator and President accounts.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>
            System Activity & Audit Logs
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Track all operations performed across Administrator, President, Financial, Registrar, Business, and Member security activities.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filter Toolbar Card */}
      <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search logs..."
                placeholder="Name, Reg No, Details..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  label="Category"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="All">All Categories</MenuItem>
                  <MenuItem value="Security">Security (Password Changes)</MenuItem>
                  <MenuItem value="President">President Activity</MenuItem>
                  <MenuItem value="Allocation">Allocation</MenuItem>
                  <MenuItem value="Savings">Savings</MenuItem>
                  <MenuItem value="Shares">Shares</MenuItem>
                  <MenuItem value="Loan">Loan</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Member Profile">Member Profile</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Performer Role Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="role-filter-label">Performer Role</InputLabel>
                <Select
                  labelId="role-filter-label"
                  label="Performer Role"
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="All">All Roles</MenuItem>
                  <MenuItem value="President">President</MenuItem>
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="Financial I">Financial I</MenuItem>
                  <MenuItem value="Financial II">Financial II</MenuItem>
                  <MenuItem value="Financial III">Financial III</MenuItem>
                  <MenuItem value="Registrar">Registrar</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="User">User / Member</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Filters */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset('custom');
                  setPage(0);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset('custom');
                  setPage(0);
                }}
              />
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12} sm={12} md={1}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchLogs} color="primary" size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="text" onClick={handleResetFilters} sx={{ textTransform: 'none' }}>
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Quick Presets Bar */}
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                Presets:
              </Typography>
              {[
                { key: 'last30', label: 'Last 30 Days' },
                { key: 'thisMonth', label: 'This Month' },
                { key: 'thisYear', label: 'This Year' },
                { key: 'all', label: 'All Time' },
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  size="small"
                  variant={activePreset === key ? 'contained' : 'outlined'}
                  onClick={() => setPresetRange(key)}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    py: 0.3,
                    px: 1.2,
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    fontWeight: 700,
                    backgroundColor: activePreset === key ? '#0B3B60' : '#ffffff',
                    borderColor: activePreset === key ? '#0B3B60' : '#CBD5E1',
                    color: activePreset === key ? '#ffffff' : '#334155',
                    boxShadow: activePreset === key ? '0 2px 6px rgba(11, 59, 96, 0.25)' : 'none',
                    '&:hover': {
                      backgroundColor: activePreset === key ? '#082942' : 'rgba(11, 59, 96, 0.06)',
                      borderColor: '#082942',
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            {(startDate || endDate) && (
              <Typography variant="caption" sx={{ color: '#0B3B60', fontWeight: 700, backgroundColor: 'rgba(11, 59, 96, 0.08)', px: 1.2, py: 0.3, borderRadius: '6px', border: '1px solid rgba(11, 59, 96, 0.2)' }}>
                Filter Period: {startDate || 'Beginning'} to {endDate || 'Today'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table Card */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#0B3B60', color: '#ffffff', py: 1.5, fontSize: '0.8125rem' } }}>
                <TableCell style={{ minWidth: 160, color: '#ffffff' }}>Timestamp</TableCell>
                <TableCell style={{ minWidth: 170, color: '#ffffff' }}>Officer / Performer</TableCell>
                <TableCell style={{ minWidth: 130, color: '#ffffff' }}>Category</TableCell>
                <TableCell style={{ minWidth: 200, color: '#ffffff' }}>Action</TableCell>
                <TableCell style={{ minWidth: 260 }}>Details</TableCell>
                <TableCell style={{ minWidth: 180 }}>Target Member</TableCell>
                <TableCell style={{ minWidth: 120 }}>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Loading audit logs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                      No activity logs found.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting the filter criteria or date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const dateStr = log.createdAt
                    ? new Date(log.createdAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })
                    : '—';

                  return (
                    <TableRow key={log._id || Math.random()} hover>
                      {/* Timestamp */}
                      <TableCell sx={{ fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {dateStr}
                      </TableCell>

                      {/* Officer */}
                      <TableCell>
                        <Stack spacing={0.3}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {log.performerName || 'Officer'}
                          </Typography>
                          <Chip
                            size="small"
                            color={getRoleChipColor(log.performerRole)}
                            label={log.performerRole || 'Officer'}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, width: 'fit-content' }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={log.category || 'General'}
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            borderColor: getCategoryColor(log.category),
                            color: getCategoryColor(log.category)
                          }}
                        />
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                        {log.action === 'PASSWORD_CHANGED' ? (
                          <Chip
                            size="small"
                            label="PASSWORD CHANGED"
                            color="error"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.70rem', fontWeight: 700 }}
                          />
                        ) : log.action === 'PASSWORD_RESET' ? (
                          <Chip
                            size="small"
                            label="PASSWORD RESET"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.70rem', fontWeight: 700 }}
                          />
                        ) : log.action?.startsWith('PRESIDENT_') ? (
                          <Chip
                            size="small"
                            label={log.action.replace('PRESIDENT_', 'PRESIDENT: ')}
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.70rem', fontWeight: 700 }}
                          />
                        ) : (
                          log.action
                        )}
                      </TableCell>

                      {/* Details */}
                      <TableCell sx={{ fontSize: '0.82rem', color: '#475569' }}>
                        {log.details}
                      </TableCell>

                      {/* Target Member */}
                      <TableCell>
                        {log.targetMemberName ? (
                          <Stack spacing={0.2}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                              {log.targetMemberName}
                            </Typography>
                            {log.targetRegistrationNo && (
                              <Typography variant="caption" color="text.secondary">
                                Reg: #{log.targetRegistrationNo}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* IP */}
                      <TableCell sx={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                        {log.ipAddress || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
}
